// Helpery do obsługi wielu dat w polach `dd` / `rd` z URL.
//
// Format URL pozostaje string, dopuszczamy dwa zapisy:
//   dd=2026-05-15                          → ["2026-05-15"]
//   dd=2026-05-15,2026-05-16,2026-05-17    → 3 daty (lista)
//   dd=2026-05-15..2026-05-31              → ciągły zakres (skrót)
//
// Skrót `from..to` jest używany przy SERIALIZACJI tylko dla ciągłych
// zakresów o długości >= 3 — krótsze listy zostają jako CSV (jest krócej).
// Parser akceptuje oba formaty oraz dowolną mieszankę (np. "...,2026-06-01..2026-06-05").
//
// Funkcje są pure, deterministyczne, posortowane rosnąco, deduplikowane.

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const RANGE_RE = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;

// Twardy limit liczby dat wyciągniętych z JEDNEGO pola (dd lub rd) z URL.
// Chroni przed DoS: złośliwy URL `dd=2020-01-01..2099-12-31` rozwinąłby się
// do ~29200 dat → rendering, useMemo, buildDatePairs (n×m) padłyby pod tym.
// 30 odpowiada limitowi kombinacji dat w buildDatePairs (maxCombos=30), więc
// więcej i tak nie ma sensu UX-owo.
export const MAX_DATES_PER_LIST = 30;

export function parseDateList(raw: string | undefined | null): string[] {
  if (!raw) return [];
  // Twarda granica długości wejścia — chroni split/regex przed pracą nad MB
  // śmieci. Schema /search już ma DATE_RAW_MAX=800, ale parser bywa wołany
  // poza route'em (np. w SearchForm z initial).
  const safe = raw.length > 4000 ? raw.slice(0, 4000) : raw;
  const out = new Set<string>();
  // Wczesny exit, kiedy zebraliśmy już MAX dat — nie rozwijamy reszty zakresów.
  outer: for (const part of safe.split(",")) {
    if (out.size >= MAX_DATES_PER_LIST) break;
    const t = part.trim();
    if (ISO_RE.test(t)) {
      out.add(t);
      continue;
    }
    const m = RANGE_RE.exec(t);
    if (m) {
      for (const d of expandRange(m[1], m[2])) {
        out.add(d);
        if (out.size >= MAX_DATES_PER_LIST) break outer;
      }
    }
  }
  // Sortujemy i obcinamy do MAX (na wypadek gdyby zakres dodał >MAX zanim
  // pętla zdążyła zerwać — np. ostatni dopuszczony zakres). Po sort
  // bierzemy NAJWCZEŚNIEJSZE daty (najbliższe użytkownikowi).
  return Array.from(out).sort().slice(0, MAX_DATES_PER_LIST);
}

export function serializeDateList(dates: string[]): string {
  const valid = Array.from(new Set(dates.filter((d) => ISO_RE.test(d)))).sort();
  if (valid.length >= 3 && isContiguousRange(valid)) {
    return `${valid[0]}..${valid[valid.length - 1]}`;
  }
  return valid.join(",");
}

// Czy lista dat to ciągły zakres (np. 15,16,17,18) — używane do formatowania
// etykiety "15–18 maj" vs "15 maj +".
export function isContiguousRange(dates: string[]): boolean {
  const sorted = [...new Set(dates)].sort();
  if (sorted.length < 2) return false;
  for (let i = 1; i < sorted.length; i++) {
    const a = new Date(sorted[i - 1] + "T00:00:00Z").getTime();
    const b = new Date(sorted[i] + "T00:00:00Z").getTime();
    if (b - a !== 86400_000) return false;
  }
  return true;
}

// Klasyfikuje surową wartość `dd`/`rd` z URL pod kątem UX.
// Zwraca:
//   - null gdy raw jest puste (nic do walidacji) lub gdy parser zwrócił >= 1 datę
//   - { code, message } gdy raw jest niepuste, ale nic z niego nie udało się
//     wyciągnąć (zły separator, odwrócony zakres, śmieci itp.).
//
// Używane do pokazania komunikatu w UI zamiast crashowania całej trasy
// na zod-regexie. Nie modyfikuje semantyki parseDateList — to wyłącznie
// warstwa diagnostyczna.
export type DateListErrorCode = "reversed-range" | "bad-format";
export interface DateListError {
  code: DateListErrorCode;
  message: string;
}

const RANGE_ANY_RE = /(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})/;

export function validateDateListRaw(raw: string | undefined | null): DateListError | null {
  if (!raw || raw.trim() === "") return null;
  const parsed = parseDateList(raw);
  if (parsed.length > 0) return null;
  // Heurystyka: jeśli wewnątrz jest składnia `from..to` z odwróconą
  // kolejnością, pokaż precyzyjniejszy komunikat.
  const m = RANGE_ANY_RE.exec(raw);
  if (m && ISO_RE.test(m[1]) && ISO_RE.test(m[2]) && m[2] < m[1]) {
    return {
      code: "reversed-range",
      message: `Odwrócony zakres dat: ${m[1]}..${m[2]}. Druga data musi być nie wcześniejsza niż pierwsza.`,
    };
  }
  return {
    code: "bad-format",
    message: "Nieprawidłowy format daty. Oczekiwany ISO YYYY-MM-DD, lista po przecinku lub zakres FROM..TO.",
  };
}

// Wygeneruj listę dni między from a to (włącznie).
export function expandRange(fromIso: string, toIso: string): string[] {
  if (!ISO_RE.test(fromIso) || !ISO_RE.test(toIso)) return [];
  const start = new Date(fromIso + "T00:00:00Z");
  const end = new Date(toIso + "T00:00:00Z");
  if (end < start) return [];
  const out: string[] = [];
  for (let t = start.getTime(); t <= end.getTime(); t += 86400_000) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

// Iloczyn kartezjański dat wylotu × dat powrotu z filtrem return >= depart.
// Bezpiecznie ograniczone do `maxCombos` — chroni przed spamowaniem API,
// gdy user wybierze np. 30×30 = 900 kombinacji.
export type DatePair = { depart: string; return?: string };

export function buildDatePairs(opts: {
  tripType: "OneWay" | "RoundTrip";
  departs: string[];
  returns: string[];
  maxCombos?: number;
}): { pairs: DatePair[]; truncated: boolean; total: number } {
  const max = opts.maxCombos ?? 30;
  const departs = parseDateList(opts.departs.join(","));
  const returns = parseDateList(opts.returns.join(","));
  const pairs: DatePair[] = [];

  if (opts.tripType === "OneWay") {
    // total = wszystkie daty wylotu (przed obcięciem do max).
    const total = departs.length;
    for (const d of departs) {
      if (pairs.length < max) pairs.push({ depart: d });
    }
    return { pairs, truncated: total > max, total };
  }

  // RoundTrip — liczymy wszystkie poprawne pary (r >= d), niezależnie od max,
  // żeby UI mógł pokazać „30 z 42 wariantów dat".
  let total = 0;
  for (const d of departs) {
    for (const r of returns) {
      if (r < d) continue;
      total++;
      if (pairs.length < max) pairs.push({ depart: d, return: r });
    }
  }
  return { pairs, truncated: total > max, total };
}
